import React, { useState, useEffect, useMemo, useRef } from 'react';
import styles from './HomeRedesign.module.scss';
import Header from './components/Header/Header';
import FeedBannerSlider from './components/FeedBannerSlider/FeedBannerSlider';
import HeroBannerSkeleton from './components/FeedBannerSlider/HeroBannerSkeleton';
import FeaturedCategories from './components/FeaturedCategories/FeaturedCategories';
import TrustBanner from './components/TrustBanner/TrustBanner';
import PortraitCollection from './components/PortraitCollection/PortraitCollection';
import CollectionSection from './components/CollectionSection/CollectionSection';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import Footer from './components/Footer/Footer';
import StoresAPI from '@api/stores-api';
import Product from '@models/product';
import Feed, { getFeedExploreAllPath } from '@models/feed';
import { generateProductDetailsUrl } from '@utils/urlUtils';

const HomeRedesign: React.FC = () => {
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [feedsLoading, setFeedsLoading] = useState(true);
  const storesAPI = useMemo(() => new StoresAPI(), []);
  const feedListPromiseRef = useRef<Promise<Feed[]> | null>(null);

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    // Set theme on body - default to dark
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  // Fetch feed list from Oneplay store (feed/list API). Deduplicated so Strict Mode double-mount doesn't call API twice.
  useEffect(() => {
    let cancelled = false;
    const promise = feedListPromiseRef.current ?? storesAPI.getFeedList();
    if (!feedListPromiseRef.current) feedListPromiseRef.current = promise;

    promise
      .then((feedList) => {
        if (!cancelled) setFeeds(feedList);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Error fetching feed list:', error);
          setFeeds([]);
        }
      })
      .finally(() => {
        if (!cancelled) setFeedsLoading(false);
        feedListPromiseRef.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [storesAPI]);

  // Helper to convert Product to collection format
  const convertProductToCollectionFormat = (product: Product): any => {
    // Generate product URL only if product has an ID
    let productUrl: string | undefined;
    if (product.id) {
      try {
        productUrl = generateProductDetailsUrl({
          id: product.id,
          title: product.title || product.extendedTitle || 'Product'
        });
      } catch (error) {
        console.warn('Failed to generate product URL:', error);
        productUrl = undefined;
      }
    }

    return {
      id: product.id,
      name: product.title || product.extendedTitle || 'Product',
      price: product.buyPriceString || `₹${product.buyPrice}`,
      originalPrice: product.displayPriceString && product.displayPrice !== product.buyPrice 
        ? product.displayPriceString 
        : undefined,
      discount: product.discountPercentage || undefined,
      gradient: product.background || 'linear-gradient(135deg, #42A5F5, #1E88E5)',
      imageUrl: product.poster || undefined,
      icon: 'fas fa-gamepad',
      tag: product.hotOffer ? 'Hot' : product.buttonDiscountText || undefined,
      link: productUrl,
      productUrl,
      isInCartQuantity: product.isInCartQuantity ?? 0
    };
  };


  // Collection sections (CHARITY, LOUNGE, AIRMILES) – static site entries, not from feed API
  const collectionSectionsData = useMemo(() => [
    { type: 'collection' as const, title: "❤️ CHARITY", subtitle: "Make a Difference", category: "charity" },
    { type: 'collection' as const, title: "✈️ LOUNGE", subtitle: "Premium Airport Lounges", category: "lounge" },
    { type: 'collection' as const, title: "🛫 AIRMILES", subtitle: "Earn & Redeem Rewards", category: "airmiles" }
  ], []);

  // Static data for CHARITY / LOUNGE / AIRMILES tiles (not from feed API)
  const collectionDataStatic: Record<string, { name: string; price: string; gradient: string; icon: string; startsFrom?: boolean; isText?: boolean }[]> = {
    charity: [
      { name: 'Read Library Project', price: 'Starts From ₹10', gradient: 'linear-gradient(135deg, #F8BBD0, #F48FB1)', icon: 'fas fa-book-reader', startsFrom: true },
      { name: 'Pavitra-Menstrual Holi...', price: 'Starts From ₹10', gradient: 'linear-gradient(135deg, #F8BBD0, #F48FB1)', icon: 'fas fa-heart', startsFrom: true },
      { name: 'Support Abused Wom...', price: 'Starts From ₹10', gradient: 'linear-gradient(135deg, #B0BEC5, #90A4AE)', icon: 'fas fa-building', startsFrom: true },
      { name: 'Provide Nutrition to Mi...', price: 'Starts From ₹10', gradient: 'linear-gradient(135deg, #FFE0B2, #FFCC80)', icon: 'fas fa-hands-helping', startsFrom: true }
    ],
    lounge: [
      { name: 'Premium Airport Lounge Access', price: 'Starts From ₹31.9', gradient: 'linear-gradient(135deg, #FF9800, #F57C00)', icon: 'fas fa-couch', startsFrom: true }
    ],
    airmiles: [
      { name: 'CitiLink Miles', price: 'Starts From ₹18,038', gradient: '#E8F5E9', icon: 'citilink', startsFrom: true, isText: true },
      { name: 'SriLanka FlySmiLes', price: 'Starts From ₹42', gradient: '#E8F5E9', icon: 'srilanka-smiles', startsFrom: true, isText: true },
      { name: 'GarudaMiles', price: 'Starts From ₹73,266', gradient: '#E8F5E9', icon: 'garuda-indonesia', startsFrom: true, isText: true },
      { name: 'Air Asia', price: 'Starts From ₹22', gradient: 'linear-gradient(135deg, #EF5350, #E53935)', icon: 'air-asia', startsFrom: true, isText: true }
    ]
  };

  return (
    <div className={styles.homeRedesign}>
      <Header />

      <div className={styles.mainContent}>
      {/* Hero banner: skeleton while loading, then API-driven header-banner (same size on all devices) */}
      {feedsLoading ? (
        <HeroBannerSkeleton />
      ) : feeds.find(f => f.type === 'header-banner')?.bannerItems?.length ? (
        <FeedBannerSlider items={feeds.find(f => f.type === 'header-banner')!.bannerItems} />
      ) : null}

      <TrustBanner />

      <FeaturedCategories />

      {/* API rails only: render in API order – portrait-banner, single-banner; header-banner already above */}
      {feeds
        .filter(feed => feed.type !== 'header-banner')
        .map((feed, index) => {
          if (feed.type === 'portrait-banner' || feed.type === 'portrait_card' || !feed.type) {
            const products = (feed.items || []).map(convertProductToCollectionFormat);
            return (
              <PortraitCollection
                key={`portrait-${feed.id ?? index}-${index}`}
                title={feed.title || 'Collection'}
                subtitle="From Oneplay Store"
                products={products}
                exploreLink={getFeedExploreAllPath(feed)}
              />
            );
          }
          if (feed.type === 'single-banner' && feed.bannerItems?.length) {
            return (
              <FeedBannerSlider key={`single-banner-${feed.id ?? index}-${index}`} items={feed.bannerItems} />
            );
          }
          return null;
        })}

      {/* Static collection sections (CHARITY, LOUNGE, AIRMILES) – hidden for now */}
      {/* <section className={styles.collectionsContainer}>
        {collectionSectionsData.map((col, idx) => (
          <CollectionSection
            key={`collection-${col.category}-${idx}`}
            title={col.title}
            subtitle={col.subtitle}
            category={col.category}
            products={collectionDataStatic[col.category] || []}
          />
        ))}
      </section> */}
    
      <Footer />
    
      <ScrollToTop />
      </div>
    </div>
  );
};

export default HomeRedesign;

    